import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Typography, Paper, Container } from '@mui/material';

interface PrivateRouteProps {
  children: React.ReactNode;
  requireSync?: boolean;
}

/** Kiểm tra enabled_2fa có yêu cầu TOTP hoặc mail_otp không */
const requiresTwoFA = (enabled2fa: string | undefined): boolean => {
  if (!enabled2fa) return false;
  return /totp|mail_otp/.test(enabled2fa);
};

/** User có tuvan_nhathau === 1 (thuộc module tư vấn nhà thầu) — dùng cho 2FA và giới hạn route */
const isUserTuvanNhathau = (user: { tuvan_nhathau?: number } | null): boolean => {
  if (user?.tuvan_nhathau === 1) return true;
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return false;
    const u = JSON.parse(raw) as { tuvan_nhathau?: number };
    return u?.tuvan_nhathau === 1;
  } catch {
    return false;
  }
};

/** User thuộc đơn vị có donvi_type = 2 (tư vấn - nhà thầu): chỉ được vào route prefix /tuvan_nhathau */
const isTuvanNhathauUnitUser = (user: { donvi_type?: number } | null): boolean => {
  return user?.donvi_type === 2;
};

/** Trang KPI/OKR (Ban): chặn rõ ràng user TVNT / đơn vị tư vấn kể cả khi URL tùy biến sau này */
const isBanOnlyKpiOkrPath = (pathname: string): boolean => {
  if (pathname === '/task-project-kpi' || pathname.startsWith('/task-project-kpi/')) return true;
  if (pathname === '/task-project-okr' || pathname.startsWith('/task-project-okr/')) return true;
  if (pathname === '/kpi' || pathname.startsWith('/kpi/')) return true;
  if (pathname === '/okr' || pathname.startsWith('/okr/')) return true;
  return false;
};

/** Các path mà user tuvan_nhathau=1 được phép vào (chỉ được vào /tuvan_nhathau và các route phụ 2FA/sync) */
const isPathAllowedForTuvanNhathau = (pathname: string): boolean => {
  return (
    pathname === '/two-fa' ||
    pathname === '/sync-account' ||
    pathname === '/tuvan_nhathau' ||
    pathname.startsWith('/tuvan_nhathau/')
  );
};

/** Redirect sau khi đăng nhập/2FA/sync: user tuvan_nhathau=1 hoặc donvi_type=2 về /tuvan_nhathau, còn lại /dashboard */
const getDefaultRedirectForUser = (user: { donvi_type?: number; tuvan_nhathau?: number } | null): string => {
  return (user && (isTuvanNhathauUnitUser(user) || isUserTuvanNhathau(user))) ? '/tuvan_nhathau' : '/dashboard';
};

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requireSync = true }) => {
  const { isAuthenticated, loading, tuvanNhathauUnitConfig, twoFaVerified } = useAuth();
  const location = useLocation();
  const user = useSelector((state: any) => state.auth.user);
  /** Chỉ user tuvan_nhathau === 1 và có tuvan_nhathau_unit_config với enabled_2fa (totp/mail_otp) mới bắt buộc 2FA */
  const need2FA = isUserTuvanNhathau(user) && requiresTwoFA(tuvanNhathauUnitConfig?.enabled_2fa);

  if (loading) {
    return (
      <Box 
        sx={{ 
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Lưu lại URL hiện tại để sau khi đăng nhập sẽ redirect về
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Kiểm tra trạng thái đồng bộ tài khoản
  if (user && user.sync_account === 0 && requireSync) {
    // Nếu user thuộc module tư vấn nhà thầu → chuyển sang trang đổi mật khẩu riêng,
    // nhưng nếu đã đang ở trang đổi mật khẩu thì cho phép vào để tránh vòng lặp redirect.
    const isTvntUser = isTuvanNhathauUnitUser(user) || isUserTuvanNhathau(user);
    const isTvntChangePasswordPath =
      location.pathname === '/tuvan_nhathau/change-password' ||
      location.pathname.startsWith('/tuvan_nhathau/setting/change-password');
    if (isTvntUser && isTvntChangePasswordPath) {
      // Cho phép render children (Trang đổi mật khẩu TVNT)
    } else {
      const target = isTvntUser ? '/tuvan_nhathau/change-password' : '/sync-account';
      return <Navigate to={target} replace />;
    }
  }
  
  // Nếu đã đồng bộ (sync_account = 1) và đang cố truy cập trang đồng bộ
  if (user && user.sync_account === 1 && location.pathname === '/sync-account') {
    return <Navigate to={getDefaultRedirectForUser(user)} replace />;
  }

  // 2FA: nếu đơn vị bật TOTP/mail_otp mà chưa verify thì chỉ cho vào /two-fa
  if (need2FA && !twoFaVerified && location.pathname !== '/two-fa') {
    return <Navigate to="/two-fa" replace />;
  }
  // Đang ở /two-fa nhưng không cần 2FA hoặc đã verify thì về trang mặc định (tuvan_nhathau -> /tuvan_nhathau)
  if (location.pathname === '/two-fa' && (!need2FA || twoFaVerified)) {
    return <Navigate to={getDefaultRedirectForUser(user)} replace />;
  }

  // User có tuvan_nhathau === 0 (hoặc không phải 1) không được vào các route có prefix /tuvan_nhathau
  const isTuvanNhathauPath = location.pathname === '/tuvan_nhathau' || location.pathname.startsWith('/tuvan_nhathau/');
  if (isTuvanNhathauPath && user && user.tuvan_nhathau !== 1) {
    return <Navigate to="/dashboard" replace />;
  }

  // User thuộc đơn vị donvi_type=2 (tư vấn - nhà thầu): chỉ được vào /tuvan_nhathau (và /two-fa, /sync-account); route khác redirect về /tuvan_nhathau
  if (isTuvanNhathauUnitUser(user) && !isPathAllowedForTuvanNhathau(location.pathname)) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // User có tuvan_nhathau === 1 chỉ được vào /tuvan_nhathau (và /two-fa, /sync-account); route khác redirect về /tuvan_nhathau
  if (isUserTuvanNhathau(user) && !isPathAllowedForTuvanNhathau(location.pathname)) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // User có tuvan_nhathau = 1 không được vào xử lý văn bản đến/đi gốc (xlvb)
  const isXlvbPath = location.pathname === '/xlvb/incoming-documents' || location.pathname.startsWith('/xlvb/incoming-documents') ||
    location.pathname === '/xlvb/outgoing-documents' || location.pathname.startsWith('/xlvb/outgoing-documents');
  if (user?.tuvan_nhathau === 1 && isXlvbPath) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // KPI/OKR chỉ Ban: user TVNT hoặc đơn vị tư vấn không được vào (route + mọi prefix /kpi, /okr nếu có)
  if (
    user &&
    isBanOnlyKpiOkrPath(location.pathname) &&
    (isUserTuvanNhathau(user) || isTuvanNhathauUnitUser(user))
  ) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  return <>{children}</>;
};

interface AdminRouteProps {
  children: React.ReactNode;
  requireSync?: boolean;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children, requireSync = true }) => {
  const { isAuthenticated, loading, tuvanNhathauUnitConfig, twoFaVerified } = useAuth();
  const location = useLocation();
  const user = useSelector((state: any) => state.auth.user);
  const roles = JSON.parse(localStorage.getItem('roles') || '[]');
  const need2FA = isUserTuvanNhathau(user) && requiresTwoFA(tuvanNhathauUnitConfig?.enabled_2fa);

  if (loading) {
    return (
      <Box 
        sx={{ 
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (user && user.sync_account === 0 && requireSync) {
    const isTvntUser = isTuvanNhathauUnitUser(user) || isUserTuvanNhathau(user);
    const isTvntChangePasswordPath =
      location.pathname === '/tuvan_nhathau/change-password' ||
      location.pathname.startsWith('/tuvan_nhathau/setting/change-password');
    if (isTvntUser && isTvntChangePasswordPath) {
      // Cho phép vào màn đổi mật khẩu TVNT trong admin route (nếu có)
    } else {
      const target = isTvntUser ? '/tuvan_nhathau/change-password' : '/sync-account';
      return <Navigate to={target} replace />;
    }
  }
  
  if (user && user.sync_account === 1 && location.pathname === '/sync-account') {
    return <Navigate to={getDefaultRedirectForUser(user)} replace />;
  }

  if (need2FA && !twoFaVerified && location.pathname !== '/two-fa') {
    return <Navigate to="/two-fa" replace />;
  }
  if (location.pathname === '/two-fa' && (!need2FA || twoFaVerified)) {
    return <Navigate to={getDefaultRedirectForUser(user)} replace />;
  }

  // User thuộc đơn vị donvi_type=2 chỉ được vào /tuvan_nhathau, không vào được admin
  if (isTuvanNhathauUnitUser(user)) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // User có tuvan_nhathau === 1 chỉ được vào /tuvan_nhathau, không vào được admin
  if (isUserTuvanNhathau(user)) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // Admin có tuvan_nhathau = 1 cũng không được vào trang settings gốc
  if (user?.tuvan_nhathau === 1 && location.pathname === '/settings') {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // Kiểm tra role ADMIN
  if (!roles.includes('ADMIN')) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: 'error.main', mb: 2 }}>
            Truy cập bị từ chối
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Bạn không có quyền truy cập trang này. Chỉ quản trị viên mới có thể vào.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Typography
              onClick={() => window.location.href = '/dashboard'}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none'
                }
              }}
            >
              Quay lại trang chủ
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return <>{children}</>;
};

interface QlvbRouteProps {
  children: React.ReactNode;
  requireSync?: boolean;
}

const QlvbRoute: React.FC<QlvbRouteProps> = ({ children, requireSync = true }) => {
  const { isAuthenticated, loading, tuvanNhathauUnitConfig, twoFaVerified } = useAuth();
  const location = useLocation();
  const user = useSelector((state: any) => state.auth.user);
  const need2FA = isUserTuvanNhathau(user) && requiresTwoFA(tuvanNhathauUnitConfig?.enabled_2fa);

  // Lấy roles từ localStorage hoặc từ user object
  let roles: string[] = [];
  try {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      const parsedUser = JSON.parse(currentUser);
      roles = parsedUser.roles || [];
    }
  } catch (error) {

  }

  if (loading) {
    return (
      <Box 
        sx={{ 
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (user && user.sync_account === 0 && requireSync) {
    const isTvntUser = isTuvanNhathauUnitUser(user) || isUserTuvanNhathau(user);
    const isTvntChangePasswordPath =
      location.pathname === '/tuvan_nhathau/change-password' ||
      location.pathname.startsWith('/tuvan_nhathau/setting/change-password');
    if (isTvntUser && isTvntChangePasswordPath) {
      // Cho phép vào màn đổi mật khẩu TVNT trong QlvbRoute (trường hợp gọi chéo)
    } else {
      const target = isTvntUser ? '/tuvan_nhathau/change-password' : '/sync-account';
      return <Navigate to={target} replace />;
    }
  }
  
  if (user && user.sync_account === 1 && location.pathname === '/sync-account') {
    return <Navigate to={getDefaultRedirectForUser(user)} replace />;
  }

  if (need2FA && !twoFaVerified && location.pathname !== '/two-fa') {
    return <Navigate to="/two-fa" replace />;
  }
  if (location.pathname === '/two-fa' && (!need2FA || twoFaVerified)) {
    return <Navigate to={getDefaultRedirectForUser(user)} replace />;
  }

  // User thuộc đơn vị donvi_type=2 chỉ được vào /tuvan_nhathau, không vào QLVB gốc
  if (isTuvanNhathauUnitUser(user)) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // User có tuvan_nhathau === 1 chỉ được vào /tuvan_nhathau, không vào được QLVB
  if (isUserTuvanNhathau(user)) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // User có tuvan_nhathau = 1 (thuộc đơn vị cấp 1) không được vào quản lý văn bản đến/đi gốc
  if (user?.tuvan_nhathau === 1) {
    return <Navigate to="/tuvan_nhathau" replace />;
  }

  // Kiểm tra role: TRUONG_PHONG_HCTC, VAN_THU_DEN, VAN_THU_DI, hoặc TRA_CUU_TOANBO_VB_DI
  const allowedRoles = ['TRUONG_PHONG_HCTC', 'VAN_THU_DEN', 'VAN_THU_DI', 'TRA_CUU_TOANBO_VB_DI'];
  const hasAllowedRole = allowedRoles.some(role => roles.includes(role));

  if (!hasAllowedRole) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: 'error.main', mb: 2 }}>
            Truy cập bị từ chối
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Bạn không có quyền truy cập trang này. Chỉ Trưởng phòng HCTC, Văn thư đến, Văn thư đi, hoặc Tra cứu toàn bộ văn bản đi mới có thể vào.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Typography
              onClick={() => window.location.href = '/dashboard'}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none'
                }
              }}
            >
              Quay lại trang chủ
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return <>{children}</>;
};

export default PrivateRoute;
export { AdminRoute, QlvbRoute }; 